import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { formatDateUS } from "../lib/format";
import {
    CaretRight,
    CaretDown,
    TreeStructure,
    CheckCircle,
    XCircle,
    Clock as ClockIcon,
    ArrowsLeftRight,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
    accepted: { bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300", dot: "bg-emerald-500" },
    declined: { bg: "bg-rose-100", text: "text-rose-800", border: "border-rose-300", dot: "bg-rose-500" },
    countered: { bg: "bg-indigo-100", text: "text-indigo-800", border: "border-indigo-300", dot: "bg-indigo-500" },
    sent: { bg: "bg-amber-50", text: "text-amber-900", border: "border-amber-300", dot: "bg-amber-500" },
    expired: { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300", dot: "bg-slate-400" },
};

function tone(s) {
    return STATUS_COLORS[s] || STATUS_COLORS.sent;
}

function statusIcon(status, sz) {
    if (status === "accepted") return <CheckCircle size={sz} weight="fill" />;
    if (status === "declined" || status === "expired") return <XCircle size={sz} weight="fill" />;
    if (status === "countered") return <ArrowsLeftRight size={sz} weight="bold" />;
    return <ClockIcon size={sz} weight="fill" />;
}

// Flatten a tree into [{node, depth}] via DFS so we avoid recursive React components.
function flatten(root) {
    const out = [];
    const stack = [{ node: root, depth: 0 }];
    while (stack.length) {
        const { node, depth } = stack.pop();
        out.push({ node, depth });
        const kids = node.children || [];
        for (let i = kids.length - 1; i >= 0; i--) {
            stack.push({ node: kids[i], depth: depth + 1 });
        }
    }
    return out;
}

function ChainCard({ chain }) {
    const [open, setOpen] = useState(chain.rounds > 1);
    const outcome = chain.outcome === "open" ? "sent" : chain.outcome;
    const t = tone(outcome);

    const titleText = chain.case_title || chain.creditor_name || "Case";
    const subText =
        (chain.creditor_name && chain.case_title ? chain.creditor_name + " · " : "") +
        chain.rounds + " round" + (chain.rounds === 1 ? "" : "s") +
        (chain.amount_owed ? " · $" + chain.amount_owed.toLocaleString() + " owed" : "");

    const nodes = flatten(chain.root);

    return (
        <Card className="overflow-hidden" data-testid={"chain-card-" + chain.case_id}>
            <button
                type="button"
                onClick={() => setOpen(function (o) { return !o; })}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                data-testid={"chain-toggle-" + chain.case_id}
            >
                <div className="flex items-center gap-3 min-w-0">
                    {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
                    <div className="text-left min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{titleText}</p>
                        <p className="text-xs text-slate-500 truncate">{subText}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <span className={"inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium " + t.bg + " " + t.text}>
                        {statusIcon(outcome, 12)}
                        {chain.outcome}
                    </span>
                    <Link
                        to={"/cases/" + chain.case_id}
                        onClick={function (e) { e.stopPropagation(); }}
                        className="text-xs text-[#2563EB] hover:underline whitespace-nowrap"
                        data-testid={"chain-open-case-" + chain.case_id}
                    >
                        Open case →
                    </Link>
                </div>
            </button>
            {open ? (
                <div className="px-5 pb-5 pt-1 bg-white border-t border-slate-100 overflow-x-auto space-y-2">
                    {nodes.map(function (entry) {
                        const node = entry.node;
                        const depth = entry.depth;
                        const nt = tone(node.status);
                        const pct = chain.amount_owed ? (node.offer_amount / chain.amount_owed) * 100 : null;
                        const dateStr = node.created_at ? formatDateUS(node.created_at) : "";
                        const isCounter = node.offer_type === "counter_offer";
                        return (
                            <div
                                key={node.token}
                                style={{ marginLeft: depth * 28 }}
                                className="relative"
                                data-testid={"chain-node-" + node.token.slice(0, 8)}
                            >
                                {depth > 0 ? (
                                    <span className="absolute -left-4 top-5 w-4 border-t border-slate-300" />
                                ) : null}
                                <div className={"inline-flex items-start gap-3 px-3 py-2.5 rounded-md border " + nt.bg + " " + nt.border}>
                                    <span className={"mt-1 h-2.5 w-2.5 rounded-full " + nt.dot} />
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 text-xs">
                                            <span className="font-mono font-semibold text-slate-900">
                                                ${(node.offer_amount || 0).toLocaleString()}
                                            </span>
                                            {pct !== null ? (
                                                <span className="text-slate-500">({pct.toFixed(0)}%)</span>
                                            ) : null}
                                            <span className={"inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium " + nt.text}>
                                                {statusIcon(node.status, 12)}
                                                {node.status}
                                            </span>
                                            {isCounter ? (
                                                <span className="text-[10px] uppercase tracking-wider text-indigo-700">counter</span>
                                            ) : null}
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-0.5">
                                            {dateStr}
                                            {node.recipient_name ? " · " + node.recipient_name : ""}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </Card>
    );
}

function Mini({ label, value, tint }) {
    const cls = tint === "emerald" ? "text-emerald-700" : "text-slate-900";
    return (
        <div className="p-4 bg-white">
            <p className="overline">{label}</p>
            <p className={"font-mono text-2xl mt-1 font-semibold " + cls}>{value}</p>
        </div>
    );
}

export default function CounterChainTree({ chains, stats }) {
    if (!chains || chains.length === 0) {
        return (
            <Card className="p-10 text-center border-dashed" data-testid="chains-empty">
                <TreeStructure size={32} className="mx-auto text-slate-400" />
                <p className="text-slate-600 mt-3">
                    No counter offers yet. As you send pay-for-deletion offers and
                    recipients counter or accept, the negotiation tree builds itself here.
                </p>
            </Card>
        );
    }
    return (
        <div className="space-y-4" data-testid="counter-chain-tree">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
                <Mini label="Negotiations" value={stats.total_chains} />
                <Mini label="Total offers sent" value={stats.total_offers} />
                <Mini label="Avg rounds" value={stats.avg_rounds} />
                <Mini label="Reached acceptance" value={stats.accepted_chains} tint="emerald" />
            </div>
            <div className="space-y-3">
                {chains.map(function (c) {
                    return <ChainCard key={c.root.token} chain={c} />;
                })}
            </div>
        </div>
    );
}
